import { useTranslations } from '@aikoa/i18n/react'
import { useRouter } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import { ActionButton, type Action } from '~/components/Actions'
import { AddPropertyFlow } from '~/components/assets/AddPropertyFlow'
import { PropertyListItem } from '~/components/assets/PropertyListItem'
import { InterestMatchBadge } from '~/components/interests/InterestMatchBadge'
import { useLeadStore } from '~/components/leads/leadStore'
import { LoadingView } from '~/components/LoadingView'
import { Detail } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import { useSetTabCount } from '~/components/ui/tabs'
import { useMatchingAssets } from '~/data/leads/useLeads'
import { cn } from '~/lib/utils'
import { validateLeadMatchScores } from './utils'

interface LeadMatchingAssetsProps {
  leadId: string
  className?: string
  onOfferAsset?: (assetId: string) => void
}

export function LeadMatchingAssets({ leadId, className, onOfferAsset }: LeadMatchingAssetsProps) {
  const { t } = useTranslations(['leads', 'common', 'property'])
  const [isAddPropertyOpen, setAddPropertyOpen] = useState(false)

  const router = useRouter()

  const { matchingAssets, matchCount, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMatchingAssets(leadId)

  const offerAssetId = useLeadStore((state) => state.offerAssetId)

  useSetTabCount('matches', !matchCount ? '!' : matchCount)
  const hasMathingAssets = matchingAssets.length > 0

  return (
    <>
      <View className={cn('gap-4', className)}>
        {isLoading && (
          <View className={cn('items-center justify-center py-4')}>
            <LoadingView />
          </View>
        )}

        {!hasMathingAssets && !isLoading && (
          <View className="flex-1 items-center justify-center gap-6 py-8">
            <View className="w-full flex-1 items-center justify-center gap-2">
              <Heading3 className="text-foreground">
                {t(($) => $.detail.matchingAssets.noMatches)}
              </Heading3>
              <Detail className="px-6 text-center text-secondary">
                {t(($) => $.detail.matchingAssets.noMatchesDescription)}
              </Detail>
            </View>
            <ActionButton
              action={{
                key: 'create-leads',
                label: t(($) => $.property.actions.addNewProperty),
                onPress: () =>
                  router.push({
                    pathname: '/(authenticated)/property/add',
                  }),
                buttonVariant: 'default',
                buttonSize: 'lg',
              }}
            />
          </View>
        )}

        {hasMathingAssets && (
          <View className="gap-2 px-3">
            {matchingAssets.map((item) => {
              const isSelected = offerAssetId === item.asset.id

              return (
                <PropertyListItem
                  key={item.asset.id}
                  asset={item.asset}
                  isSelected={isSelected}
                  organisationName={item.asset.organisation?.name}
                  realtorName={item.asset.organisation?.realtorName}
                  actions={
                    [
                      !!onOfferAsset &&
                        validateLeadMatchScores(item.matchScores) && {
                          key: 'primary',
                          label: t(($) => $.detail.matchingAssets.offerLabel),
                          onPress: () => {
                            onOfferAsset(item.asset.id)
                            return false
                          },
                          buttonSize: 'sm',
                          buttonVariant: 'primary',
                        },
                      {
                        key: 'card',
                        label: t(($) => $.detail.matchingAssets.openButton),
                        onPress: () => {
                          router.push(`/property/${item.asset.id}`)
                          return false
                        },
                        buttonSize: 'sm',
                        buttonVariant: 'outline',
                      },
                    ].filter(Boolean) as Action[]
                  }>
                  <InterestMatchBadge
                    scores={item.matchScores}
                    breakdownClassName="items-end right-0"
                  />
                </PropertyListItem>
              )
            })}

            {hasNextPage && (
              <Button
                variant="outline"
                onPress={() => void fetchNextPage()}
                loading={isFetchingNextPage}
                className="mt-3 self-center">
                {t(($) => $.common.loadMore)}
              </Button>
            )}
          </View>
        )}
      </View>

      {onOfferAsset && (
        <BottomSheet
          modalKey="lead-matching-assets-add-property"
          showCloseButton={false}
          showHandle={false}
          isOpen={isAddPropertyOpen}
          onOpenChange={setAddPropertyOpen}>
          <AddPropertyFlow
            className="max-h-[90vh] flex-1 sm:max-h-[75vh]"
            onFlowComplete={async (newAssetId) => {
              onOfferAsset(newAssetId)
              setAddPropertyOpen(false)
            }}
            onClose={() => {
              setAddPropertyOpen(false)
            }}
            isInBottomSheet
          />
        </BottomSheet>
      )}
    </>
  )
}
